/**
 * ReportIssue Page
 * Form for public users to report issues with assets
 * Requires reCAPTCHA verification
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiCheck } from 'react-icons/fi';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PublicLayout from '../components/layout/PublicLayout';
import apiService from '../services/api';
import { IssueType, PublicAssetInfo } from '../types';
import { toast } from '../utils/toast';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';
import Input from '../components/common/Input';

const ISSUE_TYPES: { value: IssueType; label: string; description: string }[] = [
  { value: 'broken', label: 'Broken', description: 'Not working at all' },
  { value: 'malfunctioning', label: 'Malfunctioning', description: 'Working poorly or intermittently' },
  { value: 'damaged', label: 'Damaged', description: 'Physically damaged' },
  { value: 'dirty', label: 'Dirty', description: 'Needs cleaning' },
  { value: 'missing', label: 'Missing', description: 'Item is missing' },
  { value: 'safety_hazard', label: 'Safety Hazard', description: 'Immediate safety concern' },
  { value: 'other', label: 'Other', description: 'Something else' },
];

const ReportIssue: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [assetInfo, setAssetInfo] = useState<PublicAssetInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [issueType, setIssueType] = useState<IssueType>('broken');
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [reporterPhone, setReporterPhone] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    loadAssetInfo();
  }, [token]);

  const loadAssetInfo = async () => {
    try {
      setLoading(true);
      const data = await apiService.getPublicAsset(token!);
      setAssetInfo(data);
    } catch (err: any) {
      console.error('Failed to load asset info:', err);
      toast.error('Asset not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!title.trim() || title.length < 3) {
      toast.error('Please enter a title (at least 3 characters)');
      return;
    }

    if (!description.trim() || description.length < 10) {
      toast.error('Please provide a detailed description (at least 10 characters)');
      return;
    }

    // Check if reCAPTCHA is ready
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not ready. Please try again in a moment.');
      return;
    }

    try {
      setSubmitting(true);

      // Execute reCAPTCHA to get token
      const recaptchaToken = await executeRecaptcha('submit_issue');

      const response = await apiService.createPublicIssue(token!, {
        title: title.trim(),
        description: description.trim(),
        issue_type: issueType,
        reporter_name: reporterName.trim() || undefined,
        reporter_email: reporterEmail.trim() || undefined,
        reporter_phone: reporterPhone.trim() || undefined,
        recaptcha_token: recaptchaToken,
      });

      // Navigate to confirmation page with tracking number
      navigate(`/public/confirm/${response.tracking_number}`);
    } catch (err: any) {
      console.error('Failed to submit issue:', err);
      toast.error(err.response?.data?.message || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PublicLayout>
    );
  }

  if (!assetInfo) {
    return null;
  }

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Report an Issue</h1>
          <p className="text-lg text-gray-600">
            Reporting issue for: <span className="font-semibold">{assetInfo.asset.name}</span>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg p-6 space-y-6">
          {/* Issue Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ISSUE_TYPES.map((type) => (
                <label
                  key={type.value}
                  className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer hover:border-blue-500 transition-colors ${
                    issueType === type.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="issueType"
                    value={type.value}
                    checked={issueType === type.value}
                    onChange={(e) => setIssueType(e.target.value as IssueType)}
                    className="sr-only"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      {issueType === type.value && (
                        <FiCheck className="w-5 h-5 text-blue-600 mr-2" />
                      )}
                      <span className="text-sm font-medium text-gray-900">{type.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Issue Title <span className="text-red-500">*</span>
            </label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Toilet not flushing"
              required
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">Brief summary of the issue</p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide as much detail as possible about the issue..."
              required
              rows={5}
              maxLength={5000}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {description.length}/5000 characters
            </p>
          </div>

          {/* Contact Information (Optional) */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Contact Information (Optional)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Provide your contact details if you'd like updates about this issue
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <Input
                  id="name"
                  type="text"
                  value={reporterName}
                  onChange={(e) => setReporterName(e.target.value)}
                  placeholder="John Doe"
                  maxLength={100}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  value={reporterEmail}
                  onChange={(e) => setReporterEmail(e.target.value)}
                  placeholder="john@example.com"
                  maxLength={100}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  type="tel"
                  value={reporterPhone}
                  onChange={(e) => setReporterPhone(e.target.value)}
                  placeholder="+1234567890"
                  maxLength={20}
                />
              </div>
            </div>
          </div>

          {/* reCAPTCHA Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <FiAlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Spam Protection</p>
                <p className="mt-1">
                  This form is protected by Google reCAPTCHA v3 to prevent automated spam.
                  By submitting this form, you acknowledge that your submission will be verified
                  according to Google's Privacy Policy and Terms of Service.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/public/asset/${token}`)}
              className="text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              disabled={submitting}
            >
              Submit Report
            </Button>
          </div>
        </form>

        {/* Safety Priority Notice */}
        {issueType === 'safety_hazard' && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <FiAlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-900">Safety Priority</p>
                <p className="text-red-700 mt-1">
                  Safety hazards are treated as high priority and will be addressed immediately.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default ReportIssue;
